import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ImagoIconButton } from './ImagoButton';

/**
 * ImagoModal - dialog.
 *
 * rgba(0,0,0,.6) overlay with a 4px blur, panel fill, 8px radius, shadow-xl,
 * 90vh cap, 16/24px gutters. The header title is 18px semibold - the panel and
 * modal heading step.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {string} [props.title]
 * @param {'sm' | 'md' | 'lg' | 'xl' | '2xl'} [props.size='md'] - 384/448/512/576/720px
 * @param {React.ReactNode} props.children - modal body
 * @param {React.ReactNode} [props.footer]
 * @param {boolean} [props.closeOnOverlayClick=true]
 * @param {boolean} [props.showCloseButton=true]
 * @param {string} [props.className]
 */
export function ImagoModal({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  footer,
  closeOnOverlayClick = true,
  showCloseButton = true,
  className = '',
  ...rest
}) {
  const handleEscapeKey = useCallback(
    (event) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll while the modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscapeKey]);

  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  const sizeStyles = {
    sm: 'max-w-[384px]',
    md: 'max-w-[448px]',
    lg: 'max-w-[512px]',
    xl: 'max-w-[576px]',
    '2xl': 'max-w-[720px]',
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-modal flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      {...rest}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 backdrop-blur-sm transition-opacity duration-base ease-standard"
        style={{ backgroundColor: 'var(--color-modal-overlay)' }}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Modal surface */}
      <div
        className={`
          relative z-10 w-full ${sizeStyles[size] || sizeStyles.md}
          rounded-lg flex flex-col max-h-[90vh]
          bg-modal-bg shadow-theme-xl
          ${className}
        `.replace(/\s+/g, ' ').trim()}
      >
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-4 shrink-0 border-b border-border">
            {title ? (
              <h2
                id="modal-title"
                className="text-base md:text-lg font-semibold text-foreground min-w-0 truncate"
              >
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showCloseButton && (
              <ImagoIconButton label="Close" size="md" onClick={onClose}>
                <X className="w-5 h-5" />
              </ImagoIconButton>
            )}
          </div>
        )}

        <div className="px-4 md:px-6 py-4 overflow-y-auto flex-1 min-h-0">
          {children}
        </div>

        {footer && (
          <div className="px-4 md:px-6 py-4 shrink-0 border-t border-border flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

/**
 * ImagoModalHeader - header section for custom modal layouts
 */
export function ImagoModalHeader({ children, className = '', ...rest }) {
  return (
    <div
      className={`px-4 md:px-6 py-4 border-b border-border ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

/**
 * ImagoModalBody - body section for custom modal layouts
 */
export function ImagoModalBody({ children, className = '', ...rest }) {
  return (
    <div className={`px-4 md:px-6 py-4 ${className}`} {...rest}>
      {children}
    </div>
  );
}

/**
 * ImagoModalFooter - footer section for custom modal layouts
 */
export function ImagoModalFooter({ children, className = '', ...rest }) {
  return (
    <div
      className={`px-4 md:px-6 py-4 border-t border-border flex items-center justify-end gap-3 ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export default ImagoModal;
